import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { DirectoryRefresh } from '@/components/ContactsTable';
import { ChevronDown, DollarSign, Search, ShieldCheck, Users } from 'lucide-react';
import { SaleDetailModal } from '@/components/SaleDetailModal';
import { DateStamp, Metric, PageIntro, Person, RecordTable, Status, QueryError, count, money } from '@/workspace/ui';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const STATUS_TONE = {
  completed: 'green', paid: 'green', success: 'green',
  pending: 'amber', processing: 'amber',
  refunded: 'red', disputed: 'red', failed: 'red', cancelled: 'red', canceled: 'red',
};
const KIND_LABEL = { purchase: 'Purchase', renewal: 'Renewal', refund: 'Refund', chargeback: 'Chargeback' };
const NOT_SETTLED = new Set(['failed', 'cancelled', 'canceled', 'pending', 'processing', 'void', 'voided', 'expired', 'declined', 'payment_failed', 'open', 'unpaid']);
const signed = s => (s.amount_signed ?? s.amount ?? 0);

// Sales — design: the prototype's Sales directory. Totals are every sale (/api/stats); the list is the newest 500.
// A matched sale opens its contact (Sales tab); an unmatched one opens the sale itself.
export default function SalesPage({ embedded = false, onSelectContact, stats = {} }) {
  const Wrap = embedded ? 'div' : 'section';

  const [detailSale, setDetailSale] = useState(null);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('all');   // all | Matched | Unmatched
  const { data: sales = [], isLoading: loading, isFetching, isError, refetch } = useQuery({
    queryKey: ['sales'],
    queryFn: () => fetch(`${BACKEND_URL}/api/sales`).then(r => { if (!r.ok) throw new Error(); return r.json(); }),
    refetchInterval: 20_000,
  });

  const matched = sales.filter(s => s.contact_id).length;
  const rows = useMemo(() => sales.filter(s => {
    if (filter !== 'all' && (s.contact_id ? 'Matched' : 'Unmatched') !== filter) return false;
    if (!search) return true;
    return [s.contact_name, s.contact_email, s.email, s.product, s.source, s.status].join(' ').toLowerCase().includes(search.trim().toLowerCase());
  }), [sales, search, filter]);

  const columns = [
    { label: 'Customer', key: 'person', className: 'sp-primary', render: s => <Person name={s.contact_name} email={s.contact_email || s.email} /> },
    { label: 'Product', key: 'product', render: s => s.product || '—' },
    { label: 'Amount', key: 'amount', render: s => <span className="sp-amount-cell"><strong className="sp-amount">{signed(s) < 0 ? '−' : ''}{money(Math.abs(signed(s)), s.currency)}</strong><small>{[KIND_LABEL[s.kind] || 'Purchase', s.source].filter(Boolean).join(' · ')}</small></span> },
    { label: 'Status', key: 'status', render: s => <Status tone={STATUS_TONE[s.status?.toLowerCase()] || 'quiet'}>{s.status || 'unknown'}</Status> },
    { label: 'Paid', key: 'date', render: s => <DateStamp value={s.created_at} /> },
    { label: 'Connection', key: 'connection', render: s => <Status tone={s.contact_id ? 'green' : 'amber'}>{s.contact_id ? 'Matched' : 'Unmatched'}</Status> },
  ];
  // Net revenue: settled purchases + renewals − refunds / chargebacks; orders = purchases only
  const settled = sales.filter(x => !NOT_SETTLED.has((x.status || '').toLowerCase()));
  const revenue = settled.reduce((n, x) => n + signed(x), 0);
  const orders = sales.filter(x => (x.kind || 'purchase') === 'purchase').length;
  const refunds = settled.filter(x => signed(x) < 0).reduce((n, x) => n - signed(x), 0);

  return <>
    {!embedded && <PageIntro title="Conversions" />}
    <div className="metrics metrics-three">
      <Metric icon={DollarSign} label="Net revenue" value={loading || isError ? '—' : money(revenue)}
        detail={refunds ? `after ${money(refunds)} refunded` : orders ? `Avg order ${money(revenue / orders)}` : 'Newest sales'} />
      <Metric icon={Users} label="Purchases" value={loading || isError ? '—' : count(orders)} detail={`${count(sales.length - orders)} renewals / refunds`} tone="aqua" />
      <Metric icon={ShieldCheck} label="Matched" value={sales.length ? `${Math.round((matched / sales.length) * 100)}%` : '—'}
        detail={`of the newest ${count(sales.length)}`} tone="green" />
    </div>
    <Wrap className={embedded ? undefined : 'panel'} aria-label="Sales list">
      <div className="table-toolbar">
        <label className="search-input"><Search size={17} aria-hidden="true" /><span className="sr-only">Search sales</span>
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search records" /></label>
        <label className="select-wrap"><span className="sr-only">Filter sales</span>
          <select value={filter} onChange={e => setFilter(e.target.value)}>
            <option value="all">All connections</option><option value="Matched">Matched</option><option value="Unmatched">Unmatched</option>
          </select><ChevronDown size={15} aria-hidden="true" /></label>
        {(search || filter !== 'all') && <button type="button" className="text-btn" onClick={() => { setSearch(''); setFilter('all'); }}>Clear filters</button>}
        <span className="toolbar-end"><DirectoryRefresh onRefresh={refetch} loading={isFetching} label="Refresh sales" /></span>
      </div>
      {isError ? <QueryError onRetry={refetch} /> : loading ? <div className="empty-state">Loading sales…</div> : (
        <RecordTable rows={rows} columns={columns} label="Sales"
          onOpen={s => (s.contact_id ? onSelectContact?.(s.contact_id) : setDetailSale(s))}
          isRowInteractive={s => !s.contact_id || !!onSelectContact}
          openName={s => `${s.contact_id ? 'View contact sales for' : 'View sale for'} ${s.contact_name || s.contact_email || s.email || s.product || 'unknown customer'}`}
          emptyText={sales.length ? 'No sales match these filters.' : 'No conversions yet. Connect your sales webhook in Connections to start matching purchases.'}
          foot={<>
            <span>Showing {count(rows.length)} of {count(sales.length)} loaded conversions · {count(matched)} matched to contacts</span>
            <span>Matched sales open the contact; unmatched ones open the sale</span>
          </>} />
      )}
    </Wrap>
    <SaleDetailModal sale={detailSale} open={!!detailSale} onClose={() => setDetailSale(null)} />
  </>;
}
